import { t } from '@bia-auction/shared-i18n';
import {
  Badge,
  Card,
  CardContent,
  CardTitle,
  EmptyState,
  Input,
  PageHeader,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  cn,
} from '@bia-auction/ui';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { AlertTriangle, ChevronRight, Search } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  FAQ,
  GLOSSARY,
  HANDBOOK,
  HELP_BY_ID,
  PARTNER,
  searchHelp,
  type HandbookTopic,
  type PartnerField,
  type PartnerTopic,
} from '../content/help';
import { roleLabel } from '../lib/labels';
import { PERMS } from '../lib/roles';

/**
 * The admin help centre (`/help`) — glossary, handbook, FAQ and the partner guide, over the
 * typed content in `src/content/help`. Deliberately not role-gated: an operator needs to know
 * an action exists even where they lack the permission to perform it (each handbook topic says
 * which roles do).
 *
 * The partner tab documents a flow that is deliberately NOT in these screens — a firm cannot
 * create a vehicle here, it arrives through the ingestion API and is moderated (ADR 0017). It
 * is split into a `business` layer (what to prepare) and a `technical` one (what to call), so
 * the fleet manager and their developer can each read only their half.
 *
 * Entries collapse with native `<details>`/`<summary>` rather than a JS accordion — it is
 * keyboard- and screen-reader-accessible without any of our code, and the browser's own
 * find-in-page still reaches the closed ones.
 */

type Tab = 'glossary' | 'handbook' | 'faq' | 'partner';

const TABS: Tab[] = ['glossary', 'handbook', 'faq', 'partner'];

/** Search is over a few hundred in-memory strings, so it runs per keystroke — there is no
 *  request to debounce, and a delay here would only lag the input. */
export function HelpPage() {
  // `strict: false` mirrors `AuditLogPage` — the page reads its own params without binding to
  // the generated route id, which also keeps it renderable in a test without a RouterProvider.
  const search = useSearch({ strict: false }) as { tab?: Tab; topic?: string };
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [opened, setOpened] = useState<ReadonlySet<string>>(() => new Set());
  const deepLinked = useRef(search.topic);

  const matches = useMemo(() => searchHelp(query), [query]);
  const searching = query.trim().length > 0;

  // A `?topic=` link may point into a tab the URL didn't name — follow the entry, not the tab.
  const tab: Tab =
    search.tab ?? (search.topic ? (HELP_BY_ID.get(search.topic)?.tab ?? 'glossary') : 'glossary');

  // Open the deep-linked entry once and bring it into view. Runs on the id, not on every
  // render, so a reader who then collapses it does not get it re-opened under them.
  useEffect(() => {
    const id = deepLinked.current;
    if (!id || !HELP_BY_ID.has(id)) return;
    setOpened((prev) => new Set(prev).add(id));
    // `?.` on the method too: jsdom does not implement scrollIntoView, and this effect runs
    // in every test that mounts the page with a `?topic=`.
    document.getElementById(id)?.scrollIntoView?.({ block: 'center' });
  }, []);

  const toggle = (id: string, open: boolean) =>
    setOpened((prev) => {
      const next = new Set(prev);
      if (open) next.add(id);
      else next.delete(id);
      return next;
    });

  /** While searching every surviving entry renders open — otherwise the reader sees a list of
   *  headings with no clue which words matched. */
  const isOpen = (id: string) => searching || opened.has(id);

  const countIn = (tabName: Tab) =>
    [...matches].filter((id) => HELP_BY_ID.get(id)?.tab === tabName).length;

  const entry = (id: string, title: string, body: React.ReactNode) => (
    <details
      key={id}
      id={id}
      open={isOpen(id)}
      onToggle={(e) => toggle(id, e.currentTarget.open)}
      className="group border-b last:border-b-0"
    >
      <summary className="flex cursor-pointer list-none items-start gap-2 rounded-sm py-3 pr-2 font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
        <ChevronRight
          aria-hidden
          className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground transition-transform group-open:rotate-90"
        />
        <span>{title}</span>
      </summary>
      <div className="space-y-3 pb-4 pl-6 text-sm text-muted-foreground">{body}</div>
    </details>
  );

  const seeAlso = (ids?: string[]) => {
    const refs = (ids ?? []).map((id) => HELP_BY_ID.get(id)).filter((e) => e !== undefined);
    if (refs.length === 0) return null;
    return (
      <p className="flex flex-wrap items-center gap-x-2 gap-y-1 pt-1 text-xs">
        <span className="font-medium text-foreground">{t('help.seeAlso')}:</span>
        {refs.map((r) => (
          <button
            key={r.id}
            type="button"
            // Deep-link inside the page: jump to the entry, open it, and switch tab if needed.
            onClick={() => {
              // Clearing the query is not cosmetic — a live search filters the panels, and the
              // entry being cited very often does NOT contain the words that were searched for
              // (that is rather the point of a cross-reference). Following the link with the
              // filter still applied would change the URL and scroll to an element that was
              // never mounted, i.e. the link would silently do nothing.
              setQuery('');
              setOpened((prev) => new Set(prev).add(r.id));
              void navigate({ to: '/help', search: { tab: r.tab, topic: r.id } });
              // The target may live in a tab that is only now mounting, so defer the scroll.
              requestAnimationFrame(() =>
                document.getElementById(r.id)?.scrollIntoView?.({ block: 'center' }),
              );
            }}
            className="rounded-sm text-tx-brand hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {r.title}
          </button>
        ))}
      </p>
    );
  };

  const warnings = (items?: string[]) =>
    items && items.length > 0 ? (
      <div className="space-y-1.5 rounded-md bg-warning-tint px-3 py-2 text-warning-tint-fg">
        {/* Uppercased in JS with the `tr` locale, never with `text-transform: uppercase`:
            CSS uppercasing of "Dikkat" yields "DIKKAT" unless the browser honours `lang="tr"`,
            and the dotted İ is exactly the casing rule docs/15 calls out. */}
        <p className="flex items-center gap-1.5 text-xs font-semibold tracking-wide">
          <AlertTriangle aria-hidden className="h-3.5 w-3.5" />
          {t('help.warnings').toLocaleUpperCase('tr')}
        </p>
        <ul className="list-disc space-y-1 pl-4">
          {items.map((w) => (
            <li key={w}>{w}</li>
          ))}
        </ul>
      </div>
    ) : null;

  /** Role chips are read from `PERMS`, never retyped — the handbook cannot contradict the
   *  permission that actually gates the button. */
  const permChips = (topic: HandbookTopic) =>
    topic.perm ? (
      <p className="flex flex-wrap items-center gap-1.5">
        <span className="text-xs font-medium text-foreground">{t('help.perm')}:</span>
        {PERMS[topic.perm].map((r) => (
          <Badge key={r} variant="secondary">
            {roleLabel(r)}
          </Badge>
        ))}
      </p>
    ) : null;

  const section = (title: string, intro: string | undefined, children: React.ReactNode) => (
    <Card>
      <CardContent className="p-4 sm:p-6">
        <CardTitle className="text-base">{title}</CardTitle>
        {intro && <p className="mt-1 text-sm text-muted-foreground">{intro}</p>}
        <div className="mt-2">{children}</div>
      </CardContent>
    </Card>
  );

  const empty = <EmptyState icon={Search} title={t('help.noResults')} />;

  const glossary = GLOSSARY.map((g) => {
    const terms = g.terms.filter((term) => matches.has(term.id));
    if (terms.length === 0) return null;
    return (
      <div key={g.id}>
        {section(
          g.title,
          undefined,
          terms.map((term) =>
            entry(
              term.id,
              term.term,
              <>
                <p className="text-foreground">{term.short}</p>
                {term.body && <p>{term.body}</p>}
                {seeAlso(term.see)}
              </>,
            ),
          ),
        )}
      </div>
    );
  }).filter(Boolean);

  const handbook = HANDBOOK.map((chapter) => {
    const topics = chapter.topics.filter((topic) => matches.has(topic.id));
    if (topics.length === 0) return null;
    return (
      <div key={chapter.id}>
        {section(
          chapter.title,
          chapter.intro,
          topics.map((topic) =>
            entry(
              topic.id,
              topic.title,
              <>
                <p>
                  <span className="font-medium text-foreground">{t('help.when')}:</span>{' '}
                  {topic.when}
                </p>
                {permChips(topic)}
                <ol className="list-decimal space-y-2 pl-4 marker:text-muted-foreground">
                  {topic.steps.map((s) => (
                    <li key={s.do}>
                      <span className="text-foreground">{s.do}</span>
                      {s.note && <span className="mt-0.5 block text-xs">{s.note}</span>}
                    </li>
                  ))}
                </ol>
                {warnings(topic.warnings)}
                {seeAlso(topic.see)}
              </>,
            ),
          ),
        )}
      </div>
    );
  }).filter(Boolean);

  /** The requirement table. Graded by consequence, not by schema optionality — `reserve` is
   *  optional to the API and "Önemli" here, because approval fails without one. */
  const fieldTable = (fields: PartnerField[]) => (
    // Scrolls inside itself: the field column holds API names that must not wrap mid-token,
    // and the page body must never scroll sideways.
    <div className="overflow-x-auto">
      <table className="w-full min-w-[32rem] border-collapse text-left text-xs">
        <thead>
          <tr className="border-b">
            <th scope="col" className="py-1.5 pr-3 font-medium text-foreground">{t('help.field')}</th>
            <th scope="col" className="py-1.5 pr-3 font-medium text-foreground">{t('help.fieldMeaning')}</th>
            <th scope="col" className="py-1.5 font-medium text-foreground">{t('help.requirement')}</th>
          </tr>
        </thead>
        <tbody>
          {fields.map((f) => (
            <tr key={f.name} className="border-b last:border-b-0 align-top">
              <td className="py-1.5 pr-3 font-mono text-[11px] text-foreground">{f.name}</td>
              <td className="py-1.5 pr-3">
                {f.label}
                {f.note && <span className="mt-0.5 block">{f.note}</span>}
              </td>
              <td className="py-1.5">
                {/* Requirement is a label, never a colour alone (docs/15 §1.2). */}
                <Badge variant={f.requirement === 'required' ? 'warning' : 'secondary'}>
                  {t(`help.req.${f.requirement}`)}
                </Badge>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const partner = PARTNER.map((s) => {
    const topics = s.topics.filter((topic) => matches.has(topic.id));
    if (topics.length === 0) return null;
    return (
      <div key={s.id} className="space-y-2">
        {/* The layer divider is the point of this tab: one page, two readers. */}
        <p className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <Badge variant={s.layer === 'technical' ? 'info' : 'secondary'}>
            {t(`help.layer.${s.layer}`)}
          </Badge>
          {s.layer === 'technical' && <span>{t('help.layer.technicalHint')}</span>}
        </p>
        {section(
          s.title,
          s.intro,
          topics.map((topic: PartnerTopic) =>
            entry(
              topic.id,
              topic.title,
              <>
                {topic.intro && <p>{topic.intro}</p>}
                {topic.steps && topic.steps.length > 0 && (
                  <ol className="list-decimal space-y-2 pl-4 marker:text-muted-foreground">
                    {topic.steps.map((step) => (
                      <li key={step.do}>
                        <span className="text-foreground">{step.do}</span>
                        {step.note && <span className="mt-0.5 block text-xs">{step.note}</span>}
                      </li>
                    ))}
                  </ol>
                )}
                {topic.fields && fieldTable(topic.fields)}
                {topic.code && (
                  <figure className="space-y-1">
                    {topic.code.caption && (
                      <figcaption className="text-xs font-medium text-foreground">
                        {topic.code.caption}
                      </figcaption>
                    )}
                    <pre className="overflow-x-auto rounded-md bg-sunken p-3 text-[11px] leading-relaxed">
                      <code>{topic.code.body}</code>
                    </pre>
                  </figure>
                )}
                {warnings(topic.warnings)}
                {seeAlso(topic.see)}
              </>,
            ),
          ),
        )}
      </div>
    );
  }).filter(Boolean);

  const faq = FAQ.map((g) => {
    const items = g.items.filter((item) => matches.has(item.id));
    if (items.length === 0) return null;
    return (
      <div key={g.id}>
        {section(
          g.title,
          undefined,
          items.map((item) =>
            entry(
              item.id,
              item.q,
              <>
                <p>{item.a}</p>
                {seeAlso(item.see)}
              </>,
            ),
          ),
        )}
      </div>
    );
  }).filter(Boolean);

  const panels: Record<Tab, React.ReactNode[]> = { glossary, handbook, faq, partner };
  // Searching one tab while the hits sit in another is the failure mode of a tabbed search;
  // say so instead of showing a bare "no results".
  const elsewhere = searching ? matches.size - countIn(tab) : 0;

  return (
    <div className="space-y-4">
      <PageHeader title={t('help.title')} subline={t('help.subline')} />

      <div className="relative max-w-md">
        <Search aria-hidden className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          className="pl-8"
          type="search"
          aria-label={t('help.search')}
          placeholder={t('help.search')}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      <Tabs value={tab} onValueChange={(v) => void navigate({ to: '/help', search: { tab: v as Tab } })}>
        <TabsList>
          {TABS.map((name) => (
            <TabsTrigger key={name} value={name}>
              {t(`help.tab.${name}`)}
              {searching && (
                <span className={cn('ml-1.5 text-xs tabular-nums', countIn(name) === 0 && 'opacity-50')}>
                  {countIn(name)}
                </span>
              )}
            </TabsTrigger>
          ))}
        </TabsList>

        {TABS.map((name) => (
          <TabsContent key={name} value={name} className="space-y-4">
            {panels[name].length > 0 ? (
              <>
                {panels[name]}
                {elsewhere > 0 && (
                  <p className="text-sm text-muted-foreground">
                    {t('help.otherTabMatches', { n: String(elsewhere) })}
                  </p>
                )}
              </>
            ) : (
              <>
                {empty}
                {elsewhere > 0 && (
                  <p className="text-center text-sm text-muted-foreground">
                    {t('help.otherTabMatches', { n: String(elsewhere) })}
                  </p>
                )}
              </>
            )}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
